import React, { useState, useRef, useEffect } from 'react';
import './ScreenShare.css';

const ScreenShare = ({ sessionId, userId, userName, userType }) => {
  const [isSharing, setIsSharing] = useState(false);
  const [stream, setStream] = useState(null);
  const [error, setError] = useState('');
  const videoRef = useRef(null);

  const startScreenShare = async () => {
    try {
      setError('');
      
      // Request screen share permission
      const screenStream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          cursor: 'always',
          displaySurface: 'monitor'
        },
        audio: true // Include system audio if available
      });

      setStream(screenStream);
      setIsSharing(true);

      // Display the shared screen in the video element
      if (videoRef.current) {
        videoRef.current.srcObject = screenStream;
      }

      // Handle when user stops sharing via browser UI
      screenStream.getVideoTracks()[0].addEventListener('ended', () => {
        stopScreenShare();
      });

    } catch (err) {
      console.error('Error starting screen share:', err);
      if (err.name === 'NotAllowedError') {
        setError('Screen sharing permission was denied. Please allow screen sharing and try again.');
      } else if (err.name === 'NotFoundError') {
        setError('No screen or window available to share.');
      } else {
        setError(`Error starting screen share: ${err.message}`);
      }
      setIsSharing(false);
    }
  };

  const stopScreenShare = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsSharing(false);
    setError('');
  };

  useEffect(() => {
    // Cleanup on unmount
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stream]);

  // Check if screen sharing is supported
  const isSupported = navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia;

  if (!isSupported) {
    return (
      <div className="screen-share-not-supported">
        <p>⚠️ Screen sharing is not supported in your browser. Please use Chrome, Firefox, Edge, or Safari (14.1+).</p>
      </div>
    );
  }

  return (
    <div className="screen-share-container">
      <div className="screen-share-header">
        <h3>🖥️ Screen Sharing</h3>
        <div className="screen-share-controls">
          {!isSharing ? (
            <button
              onClick={startScreenShare}
              className="screen-share-btn start-btn"
              title="Share your screen"
            >
              📺 Start Sharing
            </button>
          ) : (
            <button
              onClick={stopScreenShare}
              className="screen-share-btn stop-btn"
              title="Stop sharing your screen"
            >
              ⏹️ Stop Sharing
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="screen-share-error">
          {error}
        </div>
      )}

      {isSharing && (
        <div className="screen-share-preview">
          <div className="preview-header">
            <span className="sharing-indicator">
              🔴 You are sharing your screen
            </span>
            {userName && (
              <span className="sharer-name">Shared by: {userName}</span>
            )}
          </div>
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            className="screen-share-video"
          />
          <div className="preview-info">
            <p>💡 Your screen is being shared. Click "Stop Sharing" to end.</p>
            <p className="info-note">Note: Other participants will see your shared screen in the video conference.</p>
          </div>
        </div>
      )}

      {!isSharing && (
        <div className="screen-share-info">
          <p>📋 Click "Start Sharing" to preview your screen sharing.</p>
          <div className="sharing-instructions">
            <p><strong>💡 How to share your screen with the class:</strong></p>
            <ol className="sharing-steps">
              <li>Join the video conference using the "Join Live Classroom" button above</li>
              <li>Once in the video conference (Jitsi/Zoom/Meet), use the platform's screen share button</li>
              <li>Select the screen or window you want to share</li>
              <li>Your screen will be visible to all participants in the video conference</li>
            </ol>
            <p className="note-text">
              <strong>Note:</strong> This preview tool helps you test screen sharing before joining the video conference. 
              Actual screen sharing with participants happens through the video conferencing platform.
            </p>
          </div>
          <ul className="sharing-tips">
            <li>✅ Use this tool to preview what you'll share</li>
            <li>✅ Then join the video conference to share with others</li>
            <li>✅ You can share your entire screen or a specific window</li>
            {userType === 'coach' || userType === 'admin' ? (
              <li>👨‍🏫 As an instructor, share presentations, code, or demonstrations</li>
            ) : (
              <li>👨‍🎓 Students can share their work or presentations</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ScreenShare;

